import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getApi } from 'redux/apis';
import styles from './Hero.module.scss';

// Fallback starter prompts, used before the generated set lands or if the
// request fails — real things the assistant can answer, not generic filler.
const FALLBACK_ASKS = [
	"What's still unallocated?",
	'Which pay periods are unpublished?',
	'How do split rules work?',
	'What is Total Labor Burden?',
];

const greetingFor = (hour: number): string => {
	if (hour < 12) return 'Good morning';
	if (hour < 18) return 'Good afternoon';
	return 'Good evening';
};

interface HeroProps {
	onAsk: (message: string) => void;
}

// The opening state of the chat page: what fills the window before the first
// message. Purely presentational — the thread, the composer and every rule
// about sending live in ChatBubble, which renders this when it has no
// messages yet. The starter chips are generated fresh (cheap model) each time
// this mounts, so they vary per visit and per company instead of being fixed.
const Hero = ({ onAsk }: HeroProps) => {
	const firstName = useSelector((state: any) => state?.userProfile?.data?.firstName);
	const activeCompanyId = useSelector(
		(state: any) => state.companies?.selectedCompanyDetails?.company?.id ?? null
	);
	const [questions, setQuestions] = useState<string[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		getApi('/ai/starter-questions')
			.then((res) => {
				if (cancelled) return;
				const list = res?.data?.data?.questions;
				setQuestions(Array.isArray(list) && list.length ? list : FALLBACK_ASKS);
			})
			.catch(() => {
				if (!cancelled) setQuestions(FALLBACK_ASKS);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
		// Refetched when the company changes so the chips suit the active company.
	}, [activeCompanyId]);

	return (
		<div className={styles.hero}>
			<h1 className={styles.hero__greeting}>
				{greetingFor(new Date().getHours())}
				{firstName ? `, ${firstName}` : ''}
			</h1>
			<p className={styles.hero__subtitle}>
				Ask about a pay period, an allocation, or a report.
			</p>

			<div className={styles.hero__chips}>
				{loading
					? Array.from({ length: 4 }).map((_, i) => (
						<span
							key={`sk-${i}`}
							className={`${styles.hero__chip} ${styles['hero__chip--skeleton']}`}
							aria-hidden="true"
						/>
					))
					: questions.map((question) => (
						<button
							key={question}
							type="button"
							className={styles.hero__chip}
							onClick={() => onAsk(question)}
						>
							{question}
						</button>
					))}
			</div>
		</div>
	);
};

export default Hero;
